import { CircleF, GoogleMap, MarkerF } from '@react-google-maps/api'
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react'
import { useStore } from 'store'
import { getGeocode, getLatLng } from 'use-places-autocomplete'

import { circleOptions, mapOptions } from './constants'

type MapMouseEvent = google.maps.MapMouseEvent
type LatLng = google.maps.LatLngLiteral | google.maps.LatLng
type SetMapPosition = (position: google.maps.LatLngLiteral) => void
type GetMapPosition = (position: google.maps.LatLngLiteral) => void
type SetMapZoom = (value: number) => void
type SetMarkerCircle = (value: number) => void

interface IProps {
    width?: string
    radius?: number
    height?: string
    zoom?: number
    lat?: number | null
    lng?: number | null
    getMapPosition?: GetMapPosition
}

export interface IMapHandlerProps {
    map?: GoogleMap
    setMapPosition: SetMapPosition
    setMapZoom: SetMapZoom
    setMarkerCircle: SetMarkerCircle
}

export const MapComponent = forwardRef<IMapHandlerProps, IProps>(
    (props, ref) => {
        const company = useStore(state => state.company)

        const [loadedAddress, setLoadedAddress] = useState(false)

        const [position, setPosition] = useState<LatLng>(() => {
            if (props.lat && props.lng) {
                return { lat: props.lat, lng: props.lng }
            }

            return {
                lat: 0,
                lng: 0,
            } as LatLng
        })

        const [zoom, setZoom] = useState(() => {
            if (props.zoom) {
                return props.zoom
            }

            return 10
        })

        const [radius, setRadius] = useState(() => {
            if (props.radius) {
                return props.radius
            }

            return 0
        })

        const handleClick = useCallback((item: MapMouseEvent) => {
            const { latLng } = item

            if (latLng) {
                const position = { lat: latLng?.lat(), lng: latLng?.lng() }
                setPosition(position)

                if (props.getMapPosition) {
                    props.getMapPosition(position)
                }
            }
        }, [])

        const onLoadMap = useCallback((map: any) => ref && (ref = map), [ref])

        useImperativeHandle(
            ref,
            () => ({
                setMapZoom: value => setZoom(value),
                setMarkerCircle: value => setRadius(value),
                setMapPosition: position => setPosition(position),
            }),
            [setZoom, setRadius, setPosition],
        )

        useEffect(() => {
            if (loadedAddress) {
                return
            }

            if (props.lat && props.lng) {
                setZoom(18)
                setLoadedAddress(true)

                return
            }

            if (company?.Cidade?.cidade) {
                const address = `${company?.endereco}, ${company?.numero} - ${company.bairro}, ${company.Cidade.cidade}`

                getGeocode({ address })
                    .then(results => getLatLng(results[0]))
                    .then(({ lat, lng }) => {
                        setPosition({ lat, lng })
                    })
                    .catch(error => {
                        console.error('Error: ', error)
                    })

                setZoom(16)
                setLoadedAddress(true)

                return
            }

            if (!navigator.geolocation) {
                return
            }

            navigator.geolocation.getCurrentPosition(position => {
                const { latitude, longitude } = position.coords

                setPosition({
                    lat: latitude,
                    lng: longitude,
                })
                setZoom(16)
                setLoadedAddress(true)
            })
        }, [company, props, loadedAddress, position, zoom, radius])

        return (
            <GoogleMap
                center={position}
                mapContainerStyle={{ width: props.width, height: props.height }}
                onClick={handleClick}
                onLoad={onLoadMap}
                options={mapOptions}
                zoom={zoom}
            >
                <MarkerF position={position} />
                <CircleF
                    center={position}
                    radius={radius}
                    options={circleOptions}
                />
            </GoogleMap>
        )
    },
)
